import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sun, GraduationCap, Sparkles, CalendarDays, Palmtree, BadgeCheck, Check, HelpCircle, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo, siteUrl } from "@/components/Seo";
import { ChecklistLeadMagnet } from "@/components/ChecklistLeadMagnet";
import FeedbackLink from "@/components/FeedbackLink";

const FEATURES = [
  {
    icon: CalendarDays,
    title: "Weekly rhythm, planned",
    body: "Build a lesson calendar per student. Drag, drop, done.",
    span: "col-span-12 md:col-span-8",
    accent: "bg-amber-50 border-amber-100",
  },
  {
    icon: Sparkles,
    title: "Resource picks",
    body: "Recommends books, kits & apps tuned to each learning style.",
    span: "col-span-12 md:col-span-4",
    accent: "bg-emerald-50 border-emerald-100",
  },
  {
    icon: Palmtree,
    title: "Florida-first",
    body: "FES-EO, PEP, FES-UA scholarships, evaluations & dual enrollment — all in one hub.",
    span: "col-span-12 md:col-span-4",
    accent: "bg-orange-50 border-orange-100",
  },
  {
    icon: GraduationCap,
    title: "Learning-style profiles",
    body: "Tag each student Visual, Auditory, Kinesthetic or Reading — everything adapts.",
    span: "col-span-12 md:col-span-8",
    accent: "bg-rose-50 border-rose-100",
  },
];

const TIERS = [
  {
    id: "basic",
    name: "Basic",
    price: 2,
    tagline: "Get organized",
    features: ["Lesson scheduling", "Student roster", "Curated resource library", "Email support"],
    cta: "Start with Basic",
    popular: false,
  },
  {
    id: "plus",
    name: "Plus",
    price: 5,
    tagline: "Personalize learning",
    features: ["Everything in Basic", "Personalized resource recommendations", "Learning-style personalization", "Progress tracking", "Priority email"],
    cta: "Go Plus",
    popular: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: 9,
    tagline: "Full Florida suite",
    features: ["Everything in Plus", "Scholarship alerts (FES-EO, PEP)", "Compliance & portfolio helpers", "Dual-enrollment guide", "Priority chat"],
    cta: "Go Premium",
    popular: false,
  },
];

const FAQS = [
  {
    q: "Is HomeSchool Hub compliant with Florida Statute 1002.41?",
    a: "Yes. The Portfolio/Evaluation Packet generator produces the 180-day attendance log, activity records, and work samples required by FL Statute 1002.41 — all in a single PDF you can hand to an evaluator or superintendent.",
  },
  {
    q: "Does it support WRAT-5 and other standardized assessments?",
    a: "Absolutely. You can upload WRAT-5, IOWA, Stanford, or Terra Nova evaluator reports, or enter subtest scores manually. Scores blend into each student's profile and appear on the annual evaluation packet.",
  },
  {
    q: "How does the schedule builder work?",
    a: "The schedule builder reads each student's grade level, learning style, and current proficiencies, then generates a balanced weekly lesson plan you can drag-and-drop to refine.",
  },
  {
    q: "What Florida scholarships do you track?",
    a: "FES-EO, FES-UA, PEP (Personalized Education Program), Step Up For Students, and Bright Futures. New scholarship windows are surfaced automatically in the Resources Hub.",
  },
  {
    q: "How does per-student pricing work?",
    a: "Your first student pays the full monthly tier price ($2 Basic / $5 Plus / $9 Premium). Every additional student is a flat $2/month, on any tier. So a family with 3 kids on Plus pays $5 + $2 + $2 = $9/month total.",
  },
  {
    q: "Is there a limit on how many students I can add?",
    a: "Yes — up to 8 students per teacher account. This cap keeps pricing simple and prevents cross-account sharing. If you legitimately run a larger co-op and need more, contact us and we'll set you up.",
  },
  {
    q: "Do I need to enter a credit card to try it?",
    a: "No — HomeSchool Hub is 100% free for your first 90 days, no card required at signup. When you're ready to keep going, add payment info from the Billing tab.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from the Billing tab at any time. Your access remains active through the end of the current billing cycle.",
  },
];

export default function Landing() {
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/promo`;
    fetch(url).then((r) => (r.ok ? r.json() : null)).then((data) => {
      if (!cancelled && data?.active) setPromo(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "HomeSchool Hub",
      url: siteUrl,
      logo: `${siteUrl}/logo192.png`,
      description:
        "All-in-one platform for Florida homeschool teachers — scheduling, personalized resource recommendations, WRAT-5 assessments, and scholarship tracking.",
      sameAs: [],
      areaServed: { "@type": "State", name: "Florida" },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "HomeSchool Hub",
      applicationCategory: "EducationApplication",
      operatingSystem: "Web",
      url: siteUrl,
      offers: TIERS.map((t) => ({
        "@type": "Offer",
        name: t.name,
        price: t.price,
        priceCurrency: "USD",
        description: `${t.tagline} — $${t.price} per student per month`,
      })),
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "27",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <Seo
        title="HomeSchool Hub — Florida Homeschool Teacher Platform"
        description="The all-in-one platform for Florida homeschool teachers. Schedule lessons, track WRAT-5 assessments, generate compliant Portfolio/Evaluation PDFs, and stay on top of every FES-EO, FES-UA & PEP scholarship."
        path="/"
        jsonLd={jsonLd}
      />

      {/* NAV */}
      <header className="sticky top-0 z-40 glass border-b border-stone-200/60" data-testid="nav-header">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg" data-testid="brand-link">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-amber-500 text-white">
              <Sun className="w-5 h-5" />
            </span>
            HomeSchool Hub
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-stone-600" aria-label="Primary">
            <a href="#features" className="hover:text-stone-900" data-testid="nav-features">Features</a>
            <a href="#resources" className="hover:text-stone-900" data-testid="nav-resources-anchor">Resources</a>
            <a href="#checklist" className="hover:text-stone-900" data-testid="nav-checklist">Free Checklist</a>
            <a href="#pricing" className="hover:text-stone-900" data-testid="nav-pricing">Pricing</a>
            <Link to="/blog" className="hover:text-stone-900" data-testid="nav-blog">Blog</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login">
              <Button variant="ghost" className="rounded-full text-stone-700 hover:bg-stone-100">Log in</Button>
            </Link>
            <Link to="/register" data-testid="nav-register">
              <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-5 shadow-sm">
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 pt-16 md:pt-24 pb-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 uppercase tracking-[0.18em]">
              <Palmtree className="w-3.5 h-3.5" /> Built for Florida families
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              A <span className="text-amber-500">home for the resources</span> that support your homeschool journey.
            </h1>
            <p className="mt-6 text-lg text-stone-600 max-w-xl leading-relaxed">
              HomeSchool Hub is a thoughtful, all-in-one companion for Florida homeschool families. Automated lesson scheduling, curated curriculum picks, scholarship receipt tracking, student portfolios, and Florida-ready evaluation packets — all in one place.
            </p>
            <ul className="mt-6 space-y-2.5 text-[15px] text-stone-700 max-w-xl">
              <li className="flex items-start gap-2.5">
                <BadgeCheck className="w-5 h-5 text-emerald-500 flex-none mt-0.5" />
                <span><strong>Save ~$300/yr</strong> — auto-generated Evaluation Packet skips the evaluator prep fee.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <BadgeCheck className="w-5 h-5 text-emerald-500 flex-none mt-0.5" />
                <span><strong>Reclaim up to $8,000/yr per student</strong> in FES-EO scholarship dollars — every receipt organized, categorized, and ready.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <BadgeCheck className="w-5 h-5 text-emerald-500 flex-none mt-0.5" />
                <span><strong>Save 6+ hours a week</strong> with personalized weekly schedules and textbook + video picks per lesson.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <BadgeCheck className="w-5 h-5 text-emerald-500 flex-none mt-0.5" />
                <span><strong>Free forever:</strong> Letter of Intent, Evaluation Packet, and Report Card PDFs — no card, no catch.</span>
              </li>
            </ul>
            <p className="mt-5 text-sm text-stone-500 italic max-w-xl">
              Built by a Florida homeschool family, for Florida homeschool families.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-7 py-6 text-base shadow-sm hover:-translate-y-0.5 transition-all">
                  Start free — no card
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-full border-2 border-stone-200 hover:border-stone-300 hover:bg-white px-7 py-6 text-base">
                  See pricing
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-stone-500 flex-wrap">
              <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-emerald-500" /> {promo?.active ? "Free for 90 days" : "7-day free trial"}</div>
              <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-emerald-500" /> Florida statute-aware</div>
              <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-emerald-500" /> Pause 100 days for summer</div>
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-white/60 fade-up">
              <div className="absolute inset-0 bg-gradient-to-tr from-white/40 via-amber-100/10 to-transparent z-10 pointer-events-none" />
              <img
                src="https://images.unsplash.com/photo-1629360021730-3d258452c425"
                alt="Florida homeschool teacher planning lessons with her children at home"
                className="w-full h-[440px] object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block glass rounded-2xl border border-white/60 shadow-lg p-4 w-56">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-stone-500">This week</div>
              <div className="mt-1 font-display text-2xl font-bold">14 lessons</div>
              <div className="text-sm text-stone-500">across 3 students</div>
            </div>
            <div className="absolute -top-4 -right-4 hidden md:block bg-emerald-500 text-white rounded-2xl shadow-lg p-4 w-52 rotate-2">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold opacity-80">Pick</div>
              <div className="mt-1 font-display text-lg font-semibold leading-tight">Story of the World, Vol 1</div>
              <div className="text-xs opacity-90 mt-1">Perfect for auditory 4th graders</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Features</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Everything a Florida homeschool teacher needs
          </h2>
          <p className="mt-4 text-stone-600">
            Purpose-built for how you actually plan a homeschool week — not a repurposed classroom LMS.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-12 gap-4">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className={`${f.span} ${f.accent} border rounded-3xl p-8 min-h-[220px] hover:-translate-y-0.5 hover:shadow-md transition-all`}
              data-testid={`feature-card-${i}`}
            >
              <f.icon className="w-8 h-8 text-stone-900" strokeWidth={1.75} />
              <h3 className="mt-6 font-display text-xl font-semibold">{f.title}</h3>
              <p className="mt-2 text-stone-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RESOURCES PREVIEW */}
      <section id="resources" className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-sm">
            <img
              src="https://images.unsplash.com/photo-1658708827604-33772b2cff26"
              alt="Curated Florida homeschool resource library with textbooks and materials"
              className="w-full h-[380px] object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">Resources Hub</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              WRAT-5 testing, tutors, and every FL scholarship.
            </h2>
            <p className="mt-4 text-stone-600 leading-relaxed">
              A curated directory of Florida testing centers, tutor partners, curriculum options, homeschool co-ops, and every relevant scholarship — updated by us so you don't have to hunt.
            </p>
            <ul className="mt-6 space-y-3 text-stone-700">
              {[
                "WRAT-5 testing sites across Florida",
                "Vetted tutor partners (Outschool, Varsity Tutors, more)",
                "Curriculum tagged by learning style",
                "FES-EO, FES-UA, PEP & Bright Futures info",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="inline-grid place-items-center w-5 h-5 rounded-full bg-emerald-500 text-white">
                    <Check className="w-3 h-3" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CONNECT / REGIONAL COMMUNITY */}
      <section id="connect" className="mx-auto max-w-7xl px-6 py-20" data-testid="landing-connect-section">
        <div className="rounded-[2rem] bg-gradient-to-br from-amber-500 to-orange-500 text-white p-10 md:p-16 grain relative overflow-hidden">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/30 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">
                <Palmtree className="w-3.5 h-3.5" /> Regional directory
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                Connecting Florida homeschoolers, one region at a time.
              </h2>
              <p className="mt-4 text-white/90 leading-relaxed max-w-2xl">
                HomeSchool Hub brings local co-ops, verified expert tutors, and Florida-ready resources together in one place — sorted by your part of the state. Find your people in the <strong>Panhandle</strong>, <strong>North</strong>, <strong>Central</strong>, or <strong>South</strong> Florida, and know a listing is vetted before you reach out.
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-white/90 max-w-xl">
                <div className="flex items-start gap-2">
                  <BadgeCheck className="w-5 h-5 flex-none mt-0.5" />
                  <span>Local co-ops &amp; field-trip groups by region</span>
                </div>
                <div className="flex items-start gap-2">
                  <BadgeCheck className="w-5 h-5 flex-none mt-0.5" />
                  <span>Expert tutors with Florida-specific expertise</span>
                </div>
                <div className="flex items-start gap-2">
                  <BadgeCheck className="w-5 h-5 flex-none mt-0.5" />
                  <span>Verified badges after admin vetting</span>
                </div>
                <div className="flex items-start gap-2">
                  <BadgeCheck className="w-5 h-5 flex-none mt-0.5" />
                  <span>Community submissions welcome</span>
                </div>
              </div>
              <Link to="/register" data-testid="connect-cta">
                <Button className="mt-8 rounded-full bg-white text-amber-600 hover:bg-stone-100 px-6 py-5 font-semibold">
                  Explore your region <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="md:col-span-5 space-y-3">
              {[
                { key: "panhandle", label: "Panhandle", note: "Pensacola · Tallahassee" },
                { key: "north", label: "North Florida", note: "Jacksonville · Gainesville" },
                { key: "central", label: "Central Florida", note: "Orlando · Tampa Bay · Daytona" },
                { key: "south", label: "South Florida", note: "Miami · Sarasota · Ft. Myers" },
              ].map((r) => (
                <div key={r.key} className="flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur border border-white/20 px-5 py-4" data-testid={`connect-region-${r.key}`}>
                  <MapPin className="w-5 h-5 flex-none" />
                  <div>
                    <div className="font-display font-semibold">{r.label}</div>
                    <div className="text-xs text-white/80">{r.note}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3 text-sm text-white/90">
              <span>Know a co-op or tutor we should list?</span>
              <Link
                to="/suggest-a-resource"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/40 px-4 py-1.5 font-medium hover:bg-white/25 transition-colors"
                data-testid="connect-suggest-cta"
              >
                <Sparkles className="w-4 h-4" /> Suggest a resource
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <ChecklistLeadMagnet />

      {/* PRICING */}
      <section id="pricing" className="mx-auto max-w-7xl px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Pricing</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Per student, per month.
          </h2>
          <p className="mt-4 text-stone-600">Start with one student. Add more as your family grows. Cancel anytime.</p>
          {promo?.active && (
            <div
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-800"
              data-testid="promo-badge"
            >
              <BadgeCheck className="w-4 h-4" />
              <span>
                <strong className="font-semibold">Free for your first 90 days</strong>
                <span className="mx-2 text-emerald-400">·</span>
                No credit card required
              </span>
            </div>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <div
              key={t.id}
              className={`relative rounded-3xl border p-8 bg-white ${t.popular ? "border-amber-400 shadow-[0_20px_60px_rgba(245,158,11,0.15)]" : "border-stone-200 shadow-sm"}`}
              data-testid={`pricing-card-${t.id}`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-8 rounded-full bg-amber-500 text-white text-xs uppercase tracking-[0.15em] font-semibold px-3 py-1">
                  Most popular
                </div>
              )}
              <div className="font-display text-2xl font-semibold">{t.name}</div>
              <div className="text-stone-500 text-sm mt-1">{t.tagline}</div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold">${t.price}</span>
                <span className="text-stone-500">/student/mo</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-stone-700">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block mt-8" data-testid={`pricing-cta-${t.id}`}>
                <Button
                  className={`w-full rounded-full py-6 text-base ${
                    t.popular
                      ? "bg-amber-500 hover:bg-amber-600 text-white"
                      : "bg-stone-900 hover:bg-stone-800 text-white"
                  }`}
                >
                  {t.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-24">
        <div className="rounded-[2rem] p-10 md:p-16 bg-gradient-to-br from-amber-500 to-orange-500 text-white relative overflow-hidden grain">
          <div className="relative max-w-2xl">
            <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Plan next week in 15 minutes.
            </h3>
            <p className="mt-4 text-white/90 text-lg">
              Join the Florida homeschool teachers who've swapped spreadsheets and sticky notes for HomeSchool Hub.
            </p>
            <Link to="/register" data-testid="footer-cta">
              <Button className="mt-8 rounded-full bg-white text-amber-600 hover:bg-stone-100 px-7 py-6 text-base font-semibold">
                Create your free account
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </main>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-4xl px-6 pb-24" aria-labelledby="faq-heading">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">FAQ</div>
        <h2 id="faq-heading" className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Common questions from Florida homeschool parents.
        </h2>
        <div className="mt-10 space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <details
              key={f.q}
              className="group bg-white border border-stone-200 rounded-2xl px-6 py-4 open:shadow-sm transition-all"
              data-testid={`faq-item-${i}`}
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                <span className="font-display font-semibold text-stone-900 text-lg leading-snug">{f.q}</span>
                <HelpCircle className="w-5 h-5 text-amber-500 shrink-0 mt-1 transition-transform group-open:rotate-45" />
              </summary>
              <p className="mt-3 text-stone-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="border-t border-stone-200 py-8 text-center text-sm text-stone-500">
        <div className="mx-auto max-w-7xl px-6 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <span>© {new Date().getFullYear()} HomeSchool Hub — Made for Florida homeschool families.</span>
          <nav className="flex items-center gap-5" aria-label="Footer">
            <Link to="/blog" className="hover:text-stone-800" data-testid="footer-blog">Blog</Link>
            <Link to="/suggest-a-resource" className="hover:text-stone-800" data-testid="footer-suggest-resource">Suggest a resource</Link>
            <FeedbackLink variant="muted" label="Send feedback" data-testid="footer-feedback" />
            <Link to="/login" className="hover:text-stone-800" data-testid="footer-login">Log in</Link>
            <Link to="/register" className="hover:text-stone-800" data-testid="footer-register">Start free</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
